import { UserDetails, getUserProfileNoCache } from "@api/getuserProfile";
import { DefaultError, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

type UserInfo = {
    id?: string,
    icon: string,
    name: string,
    firstName: string,
    lastName: string,
    email: string,
    wedPointsBalance: number,
    isConciergeChannelCreated?: boolean,
    city?: string,
    phone?: string,
    postcode?: string,
    regionId?: number,
    weddingDate?: Date,
    isLoggedIn: boolean,
    bookedVenueSupplierId?: number,
    bookedVenueSupplierName?: string,
    hasCompletedOnboarding: boolean,
    guests?: number,
    partnerFirstName?: string,
    partnerLastName?: string,
    isDestinationWedding: boolean;
    destinationWeddingRegionId?: number;
    destinationWeddingRegionName?: string;
}

function mapServerToClientFormat(data: UserDetails | null): UserInfo {
    return {
        id: data?.UserId ?? '',
        icon: data?.ProfileImage ?? '',
        name: data?.DisplayName ?? '',
        firstName: data?.FirstName ?? '',
        lastName: data?.LastName ?? '',
        email: data?.Email ?? '',
        phone: data?.Phone ?? '',
        wedPointsBalance: data?.WedPointsBalance ?? 0,
        isConciergeChannelCreated: !!data?.IsConciergeChannelCreated,
        city: data?.City,
        postcode: data?.Postcode,
        regionId: data?.RegionId,
        weddingDate: data?.WeddingDate ? new Date(`${data?.WeddingDate}Z`) : undefined,
        isLoggedIn: !!data,
        bookedVenueSupplierId: data?.BookedVenueSupplierId,
        bookedVenueSupplierName: data?.BookedVenueSupplierName,
        hasCompletedOnboarding: data?.HasCompletedOnboarding ?? false,
        guests: data?.Guests,
        partnerFirstName: data?.PartnerFirstName,
        partnerLastName: data?.PartnerLastName,
        isDestinationWedding: data?.IsDestinationWedding ?? false,
        destinationWeddingRegionId: data?.DestinationWeddingRegionId,
        destinationWeddingRegionName: data?.DestinationWeddingRegionName,
    }
}

function mapClientToServerFormat(data: UserInfo): UserDetails {
    return {
        UserId: data.id,
        FirstName: data.firstName,
        LastName: data.lastName,
        ProfileImage: data.icon,
        DisplayName: data.name,
        Email: data.email,
        Phone: data.phone,
        WedPointsBalance: data.wedPointsBalance,
        IsConciergeChannelCreated: !!data.isConciergeChannelCreated,
        City: data.city,
        Postcode: data.postcode,
        RegionId: data.regionId,
        WeddingDate: data.weddingDate?.toISOString(),
        BookedVenueSupplierId: data.bookedVenueSupplierId,
        BookedVenueSupplierName: data.bookedVenueSupplierName,
        HasCompletedOnboarding: data?.hasCompletedOnboarding ?? false,
        Guests: data.guests,
        PartnerFirstName: data.partnerFirstName,
        PartnerLastName: data.partnerLastName,
        IsDestinationWedding: data.isDestinationWedding,
        DestinationWeddingRegionId: data.destinationWeddingRegionId,
        DestinationWeddingRegionName: data.destinationWeddingRegionName,
    }
}

export const QUERY_KEY_USER_DATA = ['userProfile'];

const useUser = (isLoggedIn: boolean = true, initialData: any = { icon: '', name: 'Guest' }) => {
    const { data, ...rest } = useQuery<UserDetails, DefaultError, UserInfo>({
        queryKey: QUERY_KEY_USER_DATA,
        queryFn: getUserProfileNoCache,
        placeholderData: mapClientToServerFormat(initialData),
        enabled: isLoggedIn,
        select: mapServerToClientFormat,
        refetchOnMount: false,
    });

    const initialUser = useMemo(() => mapServerToClientFormat(initialData), [JSON.stringify(initialData)]);

    return {
        ...rest,
        user: data ?? initialUser,
        isSignedIn: !!data?.id,
    }
}

export default useUser;